import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import styles from '../assets/inspectionRoutes.module.css';
import { inspectionRoutesService } from '../services/inspectionRoutesService';

const SortIcon = ({ direction, isActive }) => (
  <svg
    width="10"
    height="16"
    viewBox="0 0 10 16"
    style={{ marginLeft: 4, verticalAlign: "middle" }}
  >
    <g fill={isActive ? "#333" : "#bbb"}>
      {direction === "asc" ? (
        <polygon points="5,3 8,7 2,7" />
      ) : direction === "desc" ? (
        <polygon points="5,13 2,9 8,9" />
      ) : (
        <>
          <polygon points="5,3 8,7 2,7" />
          <polygon points="5,13 2,9 8,9" />
        </>
      )}
    </g>
  </svg>
);

const InspectionRoutes = () => {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [inspectionRoutes, setInspectionRoutes] = useState([]);
  const [useMockData, setUseMockData] = useState(false);
  const [entriesPerPage, setEntriesPerPage] = useState(30);
  const [tableSearch, setTableSearch] = useState('');
  const [filteredInspectionRoutes, setFilteredInspectionRoutes] = useState([]);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalRecords, setTotalRecords] = useState(0);
  const [totalPages, setTotalPages] = useState(1);
  const [columnVisibility, setColumnVisibility] = useState({
    name: true,
    buildingManager: true,
    properties: true,
    actions: true
  });
  const [showColumnVisibility, setShowColumnVisibility] = useState(false);

  // State for sorting
  const [sortField, setSortField] = useState("name");
  const [sortDirection, setSortDirection] = useState("asc");

  // Mock data generation
  const generateMockInspectionRoutes = () => {
    return [
      {
        id: 1,
        name: 'Central London Route',
        buildingManagerName: 'John Smith',
        properties: 15,
        buildingManagerId: 1
      },
      {
        id: 2,
        name: 'East London Route',
        buildingManagerName: 'Sarah Wilson',
        properties: 12,
        buildingManagerId: 2
      },
      {
        id: 3,
        name: 'North London Route',
        buildingManagerName: 'Emma Davis',
        properties: 18,
        buildingManagerId: 3
      },
      {
        id: 4,
        name: 'South London Route',
        buildingManagerName: 'James Miller',
        properties: 22,
        buildingManagerId: 4
      },
      {
        id: 5,
        name: 'West London Route',
        buildingManagerName: 'Robert Taylor',
        properties: 14,
        buildingManagerId: 5
      },
      {
        id: 6,
        name: 'North West Route',
        buildingManagerName: 'Michael Clark',
        properties: 9,
        buildingManagerId: 6
      },
      {
        id: 7,
        name: 'South East Route',
        buildingManagerName: 'Lisa Anderson',
        properties: 16,
        buildingManagerId: 7
      },
      {
        id: 8,
        name: 'City Centre Route',
        buildingManagerName: 'David Brown',
        properties: 11,
        buildingManagerId: 8
      },
      {
        id: 9,
        name: 'Suburban Route',
        buildingManagerName: 'Rachel Lee',
        properties: 13,
        buildingManagerId: 9
      },
      {
        id: 10,
        name: 'Outer London Route',
        buildingManagerName: 'Tom Wilson',
        properties: 7,
        buildingManagerId: 10
      },
      {
        id: 11,
        name: 'Outer London Route',
        buildingManagerName: 'Tom Wilson',
        properties: 7,
        buildingManagerId: 10
      }
    ];
  };

  useEffect(() => {
    loadInspectionRoutes();
  }, [useMockData]);

  // Handle sorting
  const handleSort = (field) => {
    if (sortField === field) {
      setSortDirection(sortDirection === "asc" ? "desc" : "asc");
    } else {
      setSortField(field);
      setSortDirection("asc");
    }
  };

  useEffect(() => {
    // Filter inspection routes based on search
    let filtered = [];
    if (useMockData) {
      filtered = [...inspectionRoutes].filter(route =>
        route.name.toLowerCase().includes(tableSearch.toLowerCase()) ||
        route.buildingManagerName.toLowerCase().includes(tableSearch.toLowerCase()) ||
        route.properties.toString().includes(tableSearch)
      );
    } else {
      // For real API, we could implement server-side search
      filtered = [...inspectionRoutes].filter(route =>
        route.name.toLowerCase().includes(tableSearch.toLowerCase()) ||
        route.buildingManagerName.toLowerCase().includes(tableSearch.toLowerCase()) ||
        route.properties.toString().includes(tableSearch)
      );
    }
    
    // Apply sorting
    filtered.sort((a, b) => {
      let aValue = a[sortField];
      let bValue = b[sortField];

      // Handle nested object sorting - map sortField to actual data field names
      if (sortField === "name") {
        aValue = a.name || "";
        bValue = b.name || "";
      } else if (sortField === "buildingManager") {
        aValue = a.buildingManagerName || "";
        bValue = b.buildingManagerName || "";
      } else if (sortField === "properties") {
        aValue = a.properties || 0;
        bValue = b.properties || 0;
      }

      if (aValue < bValue) return sortDirection === "asc" ? -1 : 1;
      if (aValue > bValue) return sortDirection === "asc" ? 1 : -1;
      return 0;
    });
    
    // Calculate pagination
    const total = filtered.length;
    const pages = Math.ceil(total / entriesPerPage);
    setTotalPages(pages);
    setTotalRecords(total);
    
    // Ensure current page is valid
    const validPage = Math.min(currentPage, pages || 1);
    if (validPage !== currentPage) {
      setCurrentPage(validPage);
    }
    
    // Get current page data
    const startIndex = (validPage - 1) * entriesPerPage;
    const endIndex = startIndex + entriesPerPage;
    setFilteredInspectionRoutes(filtered.slice(startIndex, endIndex));
  }, [inspectionRoutes, tableSearch, entriesPerPage, useMockData, currentPage, sortField, sortDirection]);

  const loadInspectionRoutes = async () => {
    setLoading(true);
    try {
      if (useMockData) {
        // Simulate API call delay
        await new Promise(resolve => setTimeout(resolve, 500));
        const mockData = generateMockInspectionRoutes();
        setInspectionRoutes(mockData);
        setFilteredInspectionRoutes(mockData.slice(0, entriesPerPage));
        setTotalRecords(mockData.length);
      } else {
        const response = await inspectionRoutesService.getInspectionRoutes();
        
        // Handle DataTables response format
        if (response.data && response.data.data) {
          // DataTables format
          const routes = response.data.data.map(item => ({
            id: item.id,
            name: item.name,
            buildingManagerName: item.buildingManagerName,
            properties: item.properties,
            buildingManagerId: item.buildingManagerId
          }));
          setInspectionRoutes(routes);
          setFilteredInspectionRoutes(routes.slice(0, entriesPerPage));
          setTotalRecords(response.data.recordsTotal || routes.length);
        } else if (Array.isArray(response.data)) {
          // Direct array format
          setInspectionRoutes(response.data);
          setFilteredInspectionRoutes(response.data.slice(0, entriesPerPage));
          setTotalRecords(response.data.length);
        } else {
          // Fallback
          setInspectionRoutes([]);
          setFilteredInspectionRoutes([]);
          setTotalRecords(0);
        }
      }
    } catch (error) {
      console.error('Error loading inspection routes:', error);
      toast.error('Failed to load inspection routes');
      setInspectionRoutes([]);
      setFilteredInspectionRoutes([]);
      setTotalRecords(0);
    } finally {
      setLoading(false);
    }
  };

  const handleInspectionRouteAction = (action, routeId) => {
    switch (action) {
      case 'show':
        navigate(`/inspection-routes/${routeId}`);
        break;
      case 'edit':
        navigate(`/inspection-routes/${routeId}/edit`);
        break;
      case 'delete':
        handleDeleteInspectionRoute(routeId);
        break;
      default:
        console.log('Unknown action:', action);
    }
  };

  const handleDeleteInspectionRoute = async (routeId) => {
    if (window.confirm('Are you sure you want to remove this inspection route?')) {
      try {
        if (useMockData) {
          // Remove from mock data
          const updatedRoutes = inspectionRoutes.filter(route => route.id !== routeId);
          setInspectionRoutes(updatedRoutes);
          toast.success('Inspection route successfully deleted');
        } else {
          await inspectionRoutesService.deleteInspectionRoute(routeId);
          toast.success('Inspection route successfully deleted');
          loadInspectionRoutes(); // Reload data
        }
      } catch (error) {
        console.error('Error deleting inspection route:', error);
        toast.error('Failed to delete inspection route');
      }
    }
  };

  const toggleMockData = () => {
    setUseMockData(!useMockData);
  };

  // Pagination functions
  const goToPage = (page) => {
    if (page >= 1 && page <= totalPages) {
      setCurrentPage(page);
    }
  };

  const goToPreviousPage = () => {
    goToPage(currentPage - 1);
  };

  const goToNextPage = () => {
    goToPage(currentPage + 1);
  };

  const getPageNumbers = () => {
    const pages = [];
    for (let i = 1; i <= totalPages; i++) {
      pages.push(i);
    }
    return pages;
  };

  const handleExportCSV = () => {
    try {
      const csvContent = [
        ['Name', 'Building Manager', 'Properties'],
        ...filteredInspectionRoutes.map(route => [
          route.name,
          route.buildingManagerName,
          route.properties
        ])
      ].map(row => row.map(cell => `"${cell}"`).join(',')).join('\n');
      
      const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `inspection-routes-${new Date().toISOString().split('T')[0]}.csv`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      window.URL.revokeObjectURL(url);
      toast.success('CSV exported successfully!');
    } catch (error) {
      console.error('Error exporting CSV:', error);
      toast.error('Failed to export CSV');
    }
  };

  const handleExportPDF = async () => {
    try {
      // Dynamic import for jsPDF
      let jsPDF;
      try {
        jsPDF = (await import('jspdf')).default;
      } catch (importError) {
        console.error('Failed to import jsPDF dynamically:', importError);
        // Fallback to require
        jsPDF = require('jspdf').default;
      }

      // Create new PDF document
      const doc = new jsPDF();
      
      // Add title
      doc.setFontSize(18);
      doc.setFont('helvetica', 'bold');
      doc.text('Inspection Routes', 20, 20);
      
      // Add date
      doc.setFontSize(12);
      doc.setFont('helvetica', 'normal');
      doc.text(`Generated on: ${new Date().toLocaleDateString()}`, 20, 30);
      
      // Add total records
      doc.text(`Total Records: ${inspectionRoutes.length}`, 20, 40);
      
      // Set up table parameters
      const startY = 50;
      const pageHeight = doc.internal.pageSize.height;
      const margin = 20;
      const tableWidth = doc.internal.pageSize.width - (2 * margin);
      const colWidths = [80, 60, 50]; // Building Manager, Properties, Actions
      const rowHeight = 10;
      const headerHeight = 15;
      
      // Draw table headers
      doc.setFillColor(240, 240, 240);
      doc.rect(margin, startY, tableWidth, headerHeight, 'F');
      doc.setFontSize(12);
      doc.setFont('helvetica', 'bold');
      doc.text('Building Manager', margin + 5, startY + 10);
      doc.text('Properties', margin + colWidths[0] + 5, startY + 10);
      doc.text('Actions', margin + colWidths[0] + colWidths[1] + 5, startY + 10);
      
      // Draw table data
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      let currentY = startY + headerHeight;
      let pageNumber = 1;
      
      inspectionRoutes.forEach((route, index) => {
        // Check if we need a new page
        if (currentY + rowHeight > pageHeight - 30) {
          doc.addPage();
          pageNumber++;
          currentY = 20;
          
          // Add page number
          doc.setFontSize(10);
          doc.setFont('helvetica', 'normal');
          doc.text(`Page ${pageNumber}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
        }
        
        // Draw row background (alternating colors)
        if (index % 2 === 0) {
          doc.setFillColor(248, 249, 250);
          doc.rect(margin, currentY, tableWidth, rowHeight, 'F');
        }
        
        // Draw row content
        doc.text(String(route.buildingManagerName || route.building_manager_name || 'N/A').substring(0, 25), margin + 5, currentY + 7);
        doc.text(String(route.properties || route.properties_count || '0'), margin + colWidths[0] + 5, currentY + 7);
        doc.text('Show/Edit/Remove', margin + colWidths[0] + colWidths[1] + 5, currentY + 7);
        
        currentY += rowHeight;
      });
      
      // Add page number to first page
      doc.setFontSize(10);
      doc.setFont('helvetica', 'normal');
      doc.text(`Page ${pageNumber}`, doc.internal.pageSize.width - 30, doc.internal.pageSize.height - 10);
      
      // Save the PDF
      doc.save('inspection-routes.pdf');
      
      toast.success('PDF exported successfully!');
    } catch (error) {
      console.error('Error generating PDF:', error);
      toast.error('Failed to generate PDF');
    }
  };

  const handleColumnVisibility = () => {
    setShowColumnVisibility(!showColumnVisibility);
  };

  const toggleColumnVisibility = (columnKey) => {
    setColumnVisibility(prev => ({
      ...prev,
      [columnKey]: !prev[columnKey]
    }));
  };

  // Click outside handler for column visibility dropdown
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (!event.target.closest(`.${styles.columnVisibilityDropdown}`) && 
          !event.target.closest(`.${styles.visibilityBtn}`)) {
        setShowColumnVisibility(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  if (loading) {
    return (
      <div className={styles.container}>
        <div className={styles.loading}>Loading inspection routes...</div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      {/* Debug Toggle */}
      <div className={styles.debugToggle}>
        <label>
          <input 
            type="checkbox" 
            checked={useMockData} 
            onChange={toggleMockData}
          />
          Use Mock Data
        </label>
      </div>

      {/* Breadcrumbs */}
      <div className={styles.breadcrumbs}>
        <span 
          className={styles.breadcrumbLink}
          onClick={() => navigate('/dashboard')}
        >
          Dashboard
        </span>
        <span className={styles.breadcrumbSeparator}>/</span>
        <span className={styles.currentPage}>Inspection Routes</span>
      </div>

      {/* Main Panel */}
      <div className={styles.mainPanel}>
        <div className={styles.panelTitle}>
          <div className={styles.panelTitleLeft}>
            <h3>Inspection Routes</h3>
          </div>
          <div className={styles.panelTitleRight}>
            <button 
              className={styles.btnSecondary}
              onClick={() => navigate('/inspection-routes/create')}
            >
              <i className="fa fa-plus"></i> Add Inspection Route
            </button>
          </div>
        </div>
        <div className={styles.panelBody}>
          {/* DataTables Control Bar */}
          <div className={styles.dataTableControls}>
            <div className={styles.entriesControl}>
              <label>Show entries:</label>
              <select 
                className={styles.entriesSelect}
                value={entriesPerPage}
                onChange={(e) => {
                  const newValue = Number(e.target.value);
                  setEntriesPerPage(newValue);
                  setCurrentPage(1);
                }}
              >
                <option value={10}>10</option>
                <option value={25}>25</option>
                <option value={30}>30</option>
                <option value={50}>50</option>
                <option value={75}>75</option>
                <option value={100}>100</option>
              </select>
            </div>

            <div className={styles.searchControl}>
              <label>Search:</label>
              <input
                type="text"
                className={styles.searchInput}
                placeholder="Search inspection routes..."
                value={tableSearch}
                onChange={(e) => {
                  setTableSearch(e.target.value);
                  setCurrentPage(1);
                }}
              />
            </div>

            <div className={styles.actionButtons}>
              <button 
                className={styles.exportBtn}
                onClick={handleExportCSV}
              >
                Export as CSV
              </button>
              <button 
                className={styles.exportBtn}
                onClick={handleExportPDF}
              >
                Export as PDF
              </button>
              <div className={styles.visibilityBtnContainer}>
                <button 
                  className={styles.visibilityBtn}
                  onClick={handleColumnVisibility}
                >
                  Column visibility
                </button>
                {showColumnVisibility && (
                  <div className={styles.columnVisibilityDropdown}>
                    <h4>Toggle Columns:</h4>
                    <label className={styles.columnToggle}>
                      <input
                        type="checkbox"
                        checked={columnVisibility.name}
                        onChange={() => toggleColumnVisibility('name')}
                      />
                      Name
                    </label>
                    <label className={styles.columnToggle}>
                      <input
                        type="checkbox"
                        checked={columnVisibility.buildingManager}
                        onChange={() => toggleColumnVisibility('buildingManager')}
                      />
                      Building Manager
                    </label>
                    <label className={styles.columnToggle}>
                      <input
                        type="checkbox"
                        checked={columnVisibility.properties}
                        onChange={() => toggleColumnVisibility('properties')}
                      />
                      Properties
                    </label>
                    <label className={styles.columnToggle}>
                      <input
                        type="checkbox"
                        checked={columnVisibility.actions}
                        onChange={() => toggleColumnVisibility('actions')}
                      />
                      Actions
                    </label>
                  </div>
                )}
              </div>
            </div>
          </div>

          <div className={styles.resultCount}>
            (Showing {filteredInspectionRoutes.length} of {totalRecords} results)
          </div>

          {/* Data Table */}
          <div className={styles.tableWrapper}>
            <table className={styles.dataTable}>
              <thead>
                <tr>
                  {columnVisibility.name && (
                    <th 
                      className="sortable"
                      onClick={() => handleSort("name")}
                      style={{ cursor: "pointer" }}
                    >
                      Name<SortIcon direction={sortField === "name" ? sortDirection : null} isActive={sortField === "name"} />
                    </th>
                  )}
                  {columnVisibility.buildingManager && (
                    <th 
                      className="sortable"
                      onClick={() => handleSort("buildingManager")}
                      style={{ cursor: "pointer" }}
                    >
                      Building Manager<SortIcon direction={sortField === "buildingManager" ? sortDirection : null} isActive={sortField === "buildingManager"} />
                    </th>
                  )}
                  {columnVisibility.properties && (
                    <th 
                      className="sortable"
                      onClick={() => handleSort("properties")}
                      style={{ cursor: "pointer" }}
                    >
                      Properties<SortIcon direction={sortField === "properties" ? sortDirection : null} isActive={sortField === "properties"} />
                    </th>
                  )}
                  {columnVisibility.actions && <th className={styles.actionHead}>Actions</th>}
                </tr>
              </thead>
              <tbody>
                {filteredInspectionRoutes.length > 0 ? (
                  filteredInspectionRoutes.map((route) => (
                    <tr key={route.id}>
                      {columnVisibility.name && <td>{route.name}</td>}
                      {columnVisibility.buildingManager && <td>{route.buildingManagerName}</td>}
                      {columnVisibility.properties && <td>{route.properties}</td>}
                      {columnVisibility.actions && (
                        <td className={styles.actionColumn}>
                          <button 
                            className={styles.btnShow}
                            onClick={() => handleInspectionRouteAction('show', route.id)}
                            title="Show inspection route details"
                          >
                            Show
                          </button>
                          <button 
                            className={styles.btnEdit}
                            onClick={() => handleInspectionRouteAction('edit', route.id)}
                            title="Edit inspection route"
                          >
                            Edit
                          </button>
                          <button 
                            className={styles.btnDelete}
                            onClick={() => handleInspectionRouteAction('delete', route.id)}
                            title="Remove inspection route"
                          >
                            Remove
                          </button>
                        </td>
                      )}
                    </tr>
                  ))
                ) : (
                  <tr>
                    <td colSpan={Object.values(columnVisibility).filter(Boolean).length} className={styles.noDataCell}>
                      No inspection routes found
                    </td>
                  </tr>
                )}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          {totalPages > 1 && (
            <div className={styles.pagination}>
              <div className={styles.paginationInfo}>
                Showing {((currentPage - 1) * entriesPerPage) + 1} to {Math.min(currentPage * entriesPerPage, totalRecords)} of {totalRecords} entries
              </div>
              <div className={styles.paginationControls}>
                <button 
                  className={styles.paginationBtn}
                  onClick={goToPreviousPage}
                  disabled={currentPage === 1}
                >
                  Previous
                </button>
                
                {getPageNumbers().map(page => (
                  <button
                    key={page}
                    className={`${styles.paginationBtn} ${page === currentPage ? styles.activePage : ''}`}
                    onClick={() => goToPage(page)}
                  >
                    {page}
                  </button>
                ))}
                
                <button 
                  className={styles.paginationBtn}
                  onClick={goToNextPage}
                  disabled={currentPage === totalPages}
                >
                  Next
                </button>
              </div>
            </div>
          )}
        </div>
      </div>

      {useMockData && (
        <div className={styles.demoMode}>
          <strong>Demo Mode:</strong> This is running with mock data. Toggle the checkbox in the top-right corner to test with real API.
        </div>
      )}
    </div>
  );
};

export default InspectionRoutes; 